import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { StatusChip } from '../../shared/components/StatusChip';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type {
  ReportPriority,
  ReportRunWeekday,
  ReportSchedule,
  ReportStatus,
  ReportTemplate,
} from '../../core/types/data-models';

const priorities: ReportPriority[] = ['LOW', 'MEDIUM', 'HIGH'];
const statuses: ReportStatus[] = ['ACTIVE', 'DRAFT', 'DISABLED'];
const weekdays: ReportRunWeekday[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

function clampPeriod(raw: string): number | null {
  if (raw === '') return null;
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return null;
  return Math.min(31, Math.max(1, n));
}

function clampDayOfMonth(raw: string): number {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return 1;
  return Math.min(31, Math.max(1, n));
}

/**
 * Standalone settings view for a single report template. Mirrors the
 * fields rendered by the create dialog (`NewReportTemplateDialog` /
 * `ReportTemplatesPage`'s built-in form), plus a richer scheduler
 * section that exposes the WEEKLY single-day and MONTHLY day-of-month
 * variants the dialog hides behind its DAILY default.
 *
 * The schedule shape lives in `data-models.ts` as a discriminated union;
 * we keep all three variants' state in parallel and switch the union
 * member on save so flipping between run types doesn't drop the user's
 * other selections mid-edit.
 */
export function ReportTemplateSettings() {
  const { id } = useParams();
  const templateId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const template = useQuery({
    queryKey: ['report-templates', templateId],
    queryFn: () => resources.reportTemplates.get(templateId),
    enabled: Number.isFinite(templateId),
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [lineOfBizId, setLineOfBizId] = useState<number | null>(null);
  const [relevantPeriod, setRelevantPeriod] = useState<number | null>(null);
  const [defaultPriority, setDefaultPriority] = useState<ReportPriority>('MEDIUM');
  const [status, setStatus] = useState<ReportStatus>('ACTIVE');
  const [catalogId, setCatalogId] = useState<number | null>(null);

  // Optional commerce catalog this template targets. Scoped to the template's tenant.
  const catalogs = useQuery({
    queryKey: ['commerce-catalogs', 'select', template.data?.tenantId],
    enabled: template.data?.tenantId != null,
    queryFn: () =>
      resources.commerceCatalogs.list({
        pageSize: 200,
        sort: 'name',
        filter: { tenantId: template.data!.tenantId },
      }),
  });

  // Parallel state for each schedule variant so toggling `runType` keeps
  // the user's prior weekday / day-of-month picks around.
  const [runType, setRunType] = useState<ReportSchedule['runType']>('DAILY');
  const [runWeekDays, setRunWeekDays] = useState<ReportRunWeekday[]>([
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
  ]);
  const [runWeekDay, setRunWeekDay] = useState<ReportRunWeekday>('MONDAY');
  const [runDayOfMonth, setRunDayOfMonth] = useState<number>(1);

  const [error, setError] = useState<string | null>(null);

  // Seed local state from the fetched template once it lands. Re-runs
  // when the template id changes (different row, different defaults).
  useEffect(() => {
    const t = template.data;
    if (!t) return;
    setName(t.name);
    setDescription(t.description ?? '');
    setLineOfBizId(t.lineOfBizId);
    setRelevantPeriod(t.relevantPeriod ?? null);
    setDefaultPriority(t.defaultPriority);
    setStatus(t.status);
    setCatalogId(t.catalogId ?? null);

    setRunType(t.schedule.runType);
    if (t.schedule.runType === 'DAILY') {
      setRunWeekDays(t.schedule.runWeekDays);
    } else if (t.schedule.runType === 'WEEKLY') {
      setRunWeekDay(t.schedule.runWeekDay);
    } else {
      setRunDayOfMonth(t.schedule.runDayOfMonth);
    }
  }, [template.data]);

  // Build the schedule union member that matches the current `runType`.
  const schedule: ReportSchedule = useMemo(() => {
    if (runType === 'DAILY') return { runType, runWeekDays };
    if (runType === 'WEEKLY') return { runType, runWeekDay };
    return { runType, runDayOfMonth };
  }, [runType, runWeekDays, runWeekDay, runDayOfMonth]);

  const save = useMutation({
    mutationFn: (payload: Partial<ReportTemplate>) =>
      resources.reportTemplates.update(templateId, payload),
    onSuccess: () => {
      setError(null);
      // Detail / list / sidebar dropdown all read off the same prefix.
      queryClient.invalidateQueries({ queryKey: ['report-templates'] });
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const handleSave = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (lineOfBizId == null) {
      setError('Line of business is required');
      return;
    }
    if (relevantPeriod == null) {
      setError('Relevant period is required (1 to 31 days)');
      return;
    }
    if (schedule.runType === 'DAILY' && schedule.runWeekDays.length === 0) {
      setError('Pick at least one weekday for DAILY schedules');
      return;
    }
    await save.mutateAsync({
      name: name.trim(),
      description: description.trim() || null,
      lineOfBizId,
      catalogId,
      relevantPeriod,
      defaultPriority,
      status,
      schedule,
    });
  };

  const toggleWeekday = (day: ReportRunWeekday) => {
    setRunWeekDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  if (!Number.isFinite(templateId)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Invalid report template id.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={600}>
              {template.data?.name ?? `Report template #${templateId}`}
            </Typography>
            <Chip size="small" label="Settings" />
          </Stack>
        }
        description="General settings and run schedule for this report template."
        actions={<Button onClick={() => navigate(-1)}>Back</Button>}
      />

      <Stack spacing={3} sx={{ maxWidth: 720 }}>
        {error && <Alert severity="error">{error}</Alert>}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            General
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Name"
              required
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              label="Description"
              multiline
              minRows={2}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <TextField
              label="Relevant period (days)"
              type="number"
              required
              inputProps={{ min: 1, max: 31, step: 1 }}
              helperText="Required. 1 to 31 days. Determines how far back the report's data window reaches."
              value={relevantPeriod ?? ''}
              onChange={(e) => setRelevantPeriod(clampPeriod(e.target.value))}
            />
            <FormControl fullWidth>
              <InputLabel id="rt-settings-priority-label">Default priority</InputLabel>
              <Select
                labelId="rt-settings-priority-label"
                label="Default priority"
                value={defaultPriority}
                renderValue={(v) => <StatusChip value={v as string} />}
                onChange={(e) => setDefaultPriority(e.target.value as ReportPriority)}
              >
                {priorities.map((p) => (
                  <MenuItem key={p} value={p}>
                    <StatusChip value={p} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="rt-settings-status-label">Status</InputLabel>
              <Select
                labelId="rt-settings-status-label"
                label="Status"
                value={status}
                renderValue={(v) => <StatusChip value={v as string} />}
                onChange={(e) => setStatus(e.target.value as ReportStatus)}
              >
                {statuses.map((s) => (
                  <MenuItem key={s} value={s}>
                    <StatusChip value={s} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="rt-settings-catalog-label">Commerce catalog</InputLabel>
              <Select
                labelId="rt-settings-catalog-label"
                label="Commerce catalog"
                value={catalogId ?? ''}
                onChange={(e) =>
                  setCatalogId(e.target.value === '' ? null : Number(e.target.value))
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {(catalogs.data?.data ?? []).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Schedule
          </Typography>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="rt-settings-runtype-label">Run type</InputLabel>
              <Select
                labelId="rt-settings-runtype-label"
                label="Run type"
                value={runType}
                onChange={(e) => setRunType(e.target.value as ReportSchedule['runType'])}
              >
                <MenuItem value="DAILY">Daily (pick weekdays)</MenuItem>
                <MenuItem value="WEEKLY">Weekly (single day)</MenuItem>
                <MenuItem value="MONTHLY">Monthly (day of month)</MenuItem>
              </Select>
            </FormControl>

            {runType === 'DAILY' && (
              <FormGroup row>
                {weekdays.map((d) => (
                  <FormControlLabel
                    key={d}
                    control={
                      <Checkbox
                        size="small"
                        checked={runWeekDays.includes(d)}
                        onChange={() => toggleWeekday(d)}
                      />
                    }
                    label={d.slice(0, 3)}
                  />
                ))}
              </FormGroup>
            )}

            {runType === 'WEEKLY' && (
              <FormControl fullWidth>
                <InputLabel id="rt-settings-weekday-label">Weekday</InputLabel>
                <Select
                  labelId="rt-settings-weekday-label"
                  label="Weekday"
                  value={runWeekDay}
                  onChange={(e) => setRunWeekDay(e.target.value as ReportRunWeekday)}
                >
                  {weekdays.map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {runType === 'MONTHLY' && (
              <TextField
                label="Day of month"
                type="number"
                inputProps={{ min: 1, max: 31, step: 1 }}
                helperText="1 to 31. Use 31 for end-of-month on shorter months."
                value={runDayOfMonth}
                onChange={(e) => setRunDayOfMonth(clampDayOfMonth(e.target.value))}
              />
            )}
          </Stack>
        </Paper>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="contained"
            disabled={save.isPending || template.isLoading}
            onClick={handleSave}
          >
            {save.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
